import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faTable,
  faUsers,
  faCheckCircle,
  faClipboardList,
  faFolderOpen,
  faEdit,
  faClock,
  faCalendarDay,
  faChartLine,
  faExclamationCircle,
  faArrowUp,
  faArrowDown,
  faShieldHalved,
  faImage,
  faTriangleExclamation,
  faFileLines,
  faEnvelope,
  faGraduationCap,
  faScroll,
  faRotateRight,
  faCommentDots,
  faRobot,
  faListCheck,
  faLanguage,
  faUsersCog,
  faComments,
  faTrophy,
  faCalendarDays,
  faMoneyBillWave,
  faServer
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { RecheckDecisionBody } from '../utils/recheckMessage';
import './Dashboard.css';

const ROUTE_MAP = {
  sheets: '/evaluate',
  tables: '/tables',
  students: '/students',
  'board-results': '/board-results',
  fees: '/fees',
  rubrics: '/rubrics',
  scans: '/scans',
  evaluate: '/evaluate',
  mistakes: '/mistakes',
  'blank-pages': '/blank-pages',
  rechecks: '/rechecks',
  feedback: '/feedback',
  messages: '/messages',
  requests: '/requests',
  subjective: '/papers',
  users: '/users',
  access: '/access',
  chatbot: '/chatbot',
  mcqs: '/mcqs',
  'plans-preparations': '/plans-preparations',
  ocr: '/ocr'
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getRoleBadge(role) {
  const map = {
    administrator: { label: 'Admin', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
    examiner: { label: 'Examiner', color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)' },
    teacher: { label: 'Teacher', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
    management: { label: 'Management', color: '#0D9488', bg: 'rgba(13,148,136,0.12)' }
  };
  return map[role] || { label: role, color: '#6B7280', bg: 'rgba(107,114,128,0.12)' };
}

function formatMsgTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(',', '');
}

const Dashboard = () => {
  const { currentUser, hasAccess } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeLists: 0, totalStudents: 0, gradedSheets: 0,
    activeRubrics: 0, todayGraded: 0, pendingScans: 0, avgScore: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);

  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  useEffect(() => {
    fetchDashboardStats();
    if (hasAccess('messages')) fetchMessages();
    else setMessagesLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const API_BASE_URL = window.API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/stats/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats({
          activeLists: data.activeLists || 0,
          totalStudents: data.totalStudents || 0,
          gradedSheets: data.gradedSheets || 0,
          activeRubrics: data.activeRubrics || 0,
          todayGraded: data.todayGraded || 0,
          pendingScans: data.pendingScans || 0,
          avgScore: data.avgScore || 0
        });
        setRecentActivity(data.recentActivity || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const API_BASE_URL = window.API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/messages`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(Array.isArray(data.messages) ? data.messages.slice(0, 5) : []);
      }
    } catch (err) {
      console.error('Failed to load dashboard messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const roleBadge = getRoleBadge(currentUser?.role);

  const statsCards = [
    { title: 'Active Lists', value: stats.activeLists, icon: faFolderOpen, gradient: 'linear-gradient(135deg, #2563EB, #1D4ED8)' },
    { title: 'Total Students', value: stats.totalStudents, icon: faUsers, gradient: 'linear-gradient(135deg, #0D9488, #0F766E)' },
    { title: 'Graded Sheets', value: stats.gradedSheets, icon: faCheckCircle, gradient: 'linear-gradient(135deg, #D97706, #B45309)' },
    { title: 'Active Rubrics', value: stats.activeRubrics, icon: faEdit, gradient: 'linear-gradient(135deg, #475569, #334155)' }
  ];

  const todayCards = [
    { title: 'Graded Today', value: stats.todayGraded, icon: faCalendarDay, color: '#93C5FD', trend: 'up' },
    { title: 'Pending Scans', value: stats.pendingScans, icon: faExclamationCircle, color: stats.pendingScans > 0 ? '#FCD34D' : '#6EE7B7', trend: 'neutral' },
    { title: 'Avg. Score', value: `${stats.avgScore}%`, icon: faChartLine, color: '#6EE7B7', trend: stats.avgScore >= 70 ? 'up' : 'down' }
  ];

  // Tile grid — Edupage-style icon + title cards (Messages is rendered separately).
  const quickTiles = [
    { id: 'chatbot', title: 'ChatFast', hint: 'Ask FastGrader AI', icon: faRobot, color: '#0EA5E9', access: hasAccess('chatbot') },
    { id: 'evaluate', title: 'Evaluate', hint: 'Grade answer sheets', icon: faFileAlt, color: '#2563EB', access: hasAccess('sheets') || hasAccess('evaluate') },
    { id: 'rubrics', title: 'Rubrics', hint: 'Create & manage rubrics', icon: faClipboardList, color: '#DB2777', access: hasAccess('rubrics') },
    { id: 'scans', title: 'Scans', hint: 'Upload & review scans', icon: faImage, color: '#4F46E5', access: hasAccess('scans') },
    { id: 'students', title: 'Students', hint: 'Student lists & records', icon: faGraduationCap, color: '#D97706', access: hasAccess('students') },
    { id: 'board-results', title: 'Board Results', hint: 'Extract BISE Lahore marks', icon: faTrophy, color: '#1C4D24', access: hasAccess('board-results') },
    { id: 'fees', title: 'Fees', hint: 'Collect fees on school Wi‑Fi', icon: faMoneyBillWave, color: '#0D9488', access: hasAccess('fees') },
    { id: 'mcqs', title: 'MCQs', hint: 'MCQ / OMR grading', icon: faListCheck, color: '#0891B2', access: hasAccess('mcqs') },
    { id: 'tables', title: 'Tables', hint: 'View grading results', icon: faTable, color: '#059669', access: hasAccess('tables') },
    { id: 'mistakes', title: 'Mistakes', hint: 'Common mistake patterns', icon: faTriangleExclamation, color: '#DC2626', access: hasAccess('mistakes') },
    { id: 'blank-pages', title: 'Blank pages', hint: 'Saved unexpected blank pages', icon: faFileLines, color: '#B91C1C', access: hasAccess('mistakes') },
    { id: 'rechecks', title: 'Re-checks', hint: 'Teacher re-check requests', icon: faRotateRight, color: '#EA580C', access: hasAccess('rechecks') },
    { id: 'feedback', title: 'Feedback', hint: 'Show students marked mistakes', icon: faCommentDots, color: '#0D9488', access: hasAccess('feedback') },
    { id: 'requests', title: 'Requests', hint: 'OCR, grading, and rubric chats by model', icon: faServer, color: '#334155', access: hasAccess('requests') },
    { id: 'subjective', title: 'Papers', hint: 'Create & save papers', icon: faScroll, color: '#7C3AED', access: hasAccess('subjective') },
    { id: 'plans-preparations', title: 'Plans & Prep', hint: 'Teaching plans & chapter prep', icon: faCalendarDays, color: '#7C3AED', access: hasAccess('plans-preparations') },
    { id: 'ocr', title: 'OCR', hint: 'Text recognition tools', icon: faLanguage, color: '#64748B', access: hasAccess('ocr') },
    { id: 'users', title: 'Users', hint: 'Manage users & roles', icon: faUsersCog, color: '#B91C1C', access: hasAccess('users') },
    { id: 'access', title: 'Access', hint: 'Role permissions', icon: faShieldHalved, color: '#C2410C', access: hasAccess('access') }
  ].filter((t) => t.access);

  const unreadCount = messages.filter((m) => m.recipient_user_id != null && !m.is_read).length;

  const activityIcon = (type) => {
    if (type === 'grade') return faCheckCircle;
    if (type === 'rubric') return faClipboardList;
    return faFileAlt;
  };

  return (
    <div className="db">
      <div className="db-welcome">
        <div className="db-welcome-bg" />
        <div className="db-welcome-content">
          <div className="db-welcome-top">
            <div>
              <h1 className="db-greeting">
                {getGreeting()}, {currentUser?.name || currentUser?.username}
              </h1>
              <p className="db-date">{formattedDate}</p>
            </div>
            <span className="db-role-badge" style={{ color: roleBadge.color, background: roleBadge.bg }}>
              {roleBadge.label}
            </span>
          </div>

          <div className="db-today-row">
            {todayCards.map((s, i) => (
              <div key={i} className="db-today-chip">
                <FontAwesomeIcon icon={s.icon} style={{ color: s.color }} />
                <span className="db-today-val">{loading ? '-' : s.value}</span>
                <span className="db-today-label">{s.title}</span>
                {s.trend !== 'neutral' && (
                  <FontAwesomeIcon
                    icon={s.trend === 'up' ? faArrowUp : faArrowDown}
                    className={`db-trend ${s.trend}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="db-stats">
        {statsCards.map((s, i) => (
          <div key={i} className="db-stat-card">
            <div className="db-stat-icon" style={{ background: s.gradient }}>
              <FontAwesomeIcon icon={s.icon} />
            </div>
            <div className="db-stat-info">
              <span className="db-stat-value">{loading ? '-' : s.value}</span>
              <span className="db-stat-label">{s.title}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Edupage-style home tiles: Messages + Quick Access */}
      <section className="db-section">
        <h2 className="db-section-title">Quick Access</h2>
        <div className="db-home-grid">
          {hasAccess('messages') && (
            <button
              type="button"
              className="db-home-tile db-home-tile--messages"
              onClick={() => navigate('/messages')}
            >
              <div className="db-home-tile-head">
                <span className="db-home-tile-icon" style={{ color: '#EA580C' }}>
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <div className="db-home-tile-titles">
                  <span className="db-home-tile-title">
                    Messages
                    {unreadCount > 0 && (
                      <span className="db-home-msg-badge">{unreadCount}</span>
                    )}
                  </span>
                  <span className="db-home-tile-hint">
                    {messagesLoading
                      ? 'Loading…'
                      : messages.length
                        ? `${messages.length} latest`
                        : 'No new messages'}
                  </span>
                </div>
                <FontAwesomeIcon icon={faComments} className="db-home-tile-corner" />
              </div>

              <div className="db-home-msg-list">
                {messagesLoading ? (
                  <div className="db-home-msg-empty">Loading messages…</div>
                ) : messages.length === 0 ? (
                  <div className="db-home-msg-empty">No messages yet</div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className="db-home-msg-item">
                      <div className="db-home-msg-meta">
                        <span className="db-home-msg-author">
                          {(msg.full_name || msg.username || 'User').toUpperCase()}
                          {msg.message_type === 'recheck_decision' ? ' — Re-check' : ''}
                          {msg.message_type === 'regrade_reply' ? ' — Re-grade' : ''}
                        </span>
                        <span className="db-home-msg-time">{formatMsgTime(msg.created_at)}</span>
                      </div>
                      <div className="db-home-msg-text">
                        {msg.message_type === 'recheck_decision'
                          ? <RecheckDecisionBody text={msg.message_text} />
                          : String(msg.message_text || '').replace(/\s+/g, ' ').trim().slice(0, 90)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </button>
          )}

          {quickTiles.map((tile) => (
            <button
              key={tile.id}
              type="button"
              className="db-home-tile"
              onClick={() => navigate(ROUTE_MAP[tile.id] || '/dashboard')}
            >
              <span className="db-home-tile-icon" style={{ color: tile.color }}>
                <FontAwesomeIcon icon={tile.icon} />
              </span>
              <div className="db-home-tile-titles">
                <span className="db-home-tile-title">{tile.title}</span>
                <span className="db-home-tile-hint">{tile.hint}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="db-section">
        <h2 className="db-section-title">
          <FontAwesomeIcon icon={faClock} className="db-section-icon" />
          Recent Activity
        </h2>
        <div className="db-activity">
          {recentActivity.length > 0 ? (
            recentActivity.slice(0, 6).map((a, i) => (
              <div key={i} className="db-activity-item">
                <div className="db-activity-dot" style={{ background: a.color || '#2563EB' }} />
                <div
                  className="db-activity-icon"
                  style={{ color: a.color || '#2563EB', background: `${a.color || '#2563EB'}12` }}
                >
                  <FontAwesomeIcon icon={activityIcon(a.type)} />
                </div>
                <div className="db-activity-body">
                  <p className="db-activity-msg">{a.message}</p>
                  <span className="db-activity-time">{a.time}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="db-activity-empty">
              <div className="db-activity-empty-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <p>No recent activity</p>
              <span>Start grading to see your activity here</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
